import { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import styled from 'styled-components'

import { IconOptionsReport, IconOptionsThrash } from 'assets/svg/sprite'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import Report from 'components/UI/Modal/Report/Report'
import { useModalContext } from 'context/modalContext'
import { useOutsideAlerter, useReportPost } from 'helpers/hooks'
import { useGlobalStore } from 'store/global'
import screens from 'styles/screens'
import { type IOption } from 'types/iTypes'

interface OptionsDropdownProps {
    role: string
    content: string
    isOptionsNavOpen: boolean
    postId: string
    setIsOptionsNavOpen: (_value: boolean) => void
    options: IOption[] | null
    deleteFn?: () => void
    withoutBottomNavigation?: boolean
}

export const OptionsDropdown = ({
    role,
    isOptionsNavOpen,
    postId,
    setIsOptionsNavOpen,
    options,
    deleteFn,
    content,
    withoutBottomNavigation = false
}: OptionsDropdownProps) => {
    const modalData = useModalContext()
    const optionsRef = useRef<HTMLDivElement>(null)
    const queryClient = useQueryClient()
    const { t } = useTranslation()
    const { accountSetupPopup, setAccountSetupPopup } = useGlobalStore()

    const itemTitle = () => {
        switch (content) {
            case 'post':
                return t('deletePost')
            case 'premium':
                return t('Delete Post')
            case 'story':
                return t('deleteStory')
            case 'message':
                return t('deleteMessage')
            case 'stream':
                return t('Delete Stream')
            case 'listCreator':
                return 'Remove from List'
            default:
                return t('delete')   
        }
    }

    useOutsideAlerter(optionsRef, () => {
        setIsOptionsNavOpen(false)
    })

    useEffect(() => {
        if (isOptionsNavOpen) {
            setAccountSetupPopup({
                visible: false
            })
        } else {
            if (accountSetupPopup.shouldShow) {
                setAccountSetupPopup({
                    visible: true
                })
            }
        }
    }, [isOptionsNavOpen])

/*     const cachedData = queryClient.getQueryData<ReportType[]>([
        'reportTypes',
        'content'
    ])

    const { data: reportTypesData } = useQuery(
        ['reportTypes', 'content'],
        async () => await getReportTypes('content'),
        {
            staleTime: 5000,
            enabled: !cachedData
        }
    ) */

    const { reportPostMutation } = useReportPost()

    const deleteModalContent = () => {
        switch (content) {
            case 'post':
                return {
                    title: t('deletePost'),
                    body: t('areYouSureYouWantToDeleteThisPost')
                }
            case 'listCreator':
                return {
                    title: 'Remove Creator',
                    body: 'Are you sure you want to remove this creator from the list?'
                }
            case 'premium':
                return {
                    title: t('deletePost'),
                    body: t('areYouSureYouWantToDeleteThisPost')
                }

            case 'story':
                return {
                    title: t('deleteStory'),
                    body: t('areYouSureYouWantToDeleteThisStory')
                }

            case 'message':
                return {
                    title: t('deleteMessage'),
                    body: t('areYouSureYouWantToDeleteThisMessage')
                }

            case 'stream':
                return {
                    title: t('deleteStream'),
                    body: t('areYouSureYouWantToDeleteThisStream')
                }

            case 'list':
                return {
                    title: t('deleteList'),
                    body: t('areYouSureYouWantToDeleteThisList')
                }

            default:
                return {
                    title: t('delete'),
                    body: t('areYouSureYouWantToDeleteThis')
                }
        }
    }

    const handlePostDelete = () => {
        const { title, body } = deleteModalContent()

        modalData.addModal(
            title,
            <ConfirmModal
                body={body}
                confirmFn={() => {
                    setIsOptionsNavOpen(false)
                    deleteFn?.()
                }}
            />
        )
    }

    const handlePostReport = () => {
    // close dropdown immediately
    setIsOptionsNavOpen(false)

    modalData.addModal(
        t('reportPost'),
        <Report
        type='content'
        confirmFn={(message: string, reportType: number) => {
            reportPostMutation.mutate(
            { postId, message, reportType },
            {
                onSuccess: () => {
                modalData.clearModal()
                },
            }
            )
        }}
        closeFn={() => {
            modalData.clearModal()
        }}
        />
    )
    }

/*     useEffect(() => {
        const prefetchPlaylists = async () => {
            await queryClient.prefetchQuery({
                queryKey: ['getPlaylists'],
                queryFn: async () => await getPlaylists()
            })
        }

        isOptionsNavOpen && prefetchPlaylists()
    }, [isOptionsNavOpen, queryClient]) */

    return (
        <>
            {isOptionsNavOpen && <BackgroundContainer />}
            <div className='post__options'>
                <OptionsDropdownWrapper
                    ref={optionsRef}
                    className={`post__options__dropdown ${
                        isOptionsNavOpen
                            ? 'post__options__dropdown--active'
                            : ''
                    } ${
                        !options?.length
                            ? 'post__options__dropdown--single'
                            : ''
                    }`}
                    $withoutBottomNavigation={withoutBottomNavigation}
                >
                    <div className='post__options__dropdown__section'>
                        {options &&
                            options.length > 0 &&
                            options.map((opt: IOption, key: number) => (
                                <div
                                    key={key}
                                    className={`post__options__option ${
                                        opt.disabled
                                            ? 'post__options__option--disabled'
                                            : ''
                                    }`}
                                    onClick={() => {
                                        opt.action(postId)
                                    }}
                                >
                                    {typeof opt.icon === 'string' ? (
                                        <img src={opt.icon} alt={opt.text} />
                                    ) : (
                                        opt.icon
                                    )}
                                    <span>{opt.text}</span>
                                </div>
                            ))}
                    </div>
                    {options && options.length > 0 && (
                        <div className='separator__line'></div>
                    )}
                    <div className='post__options__dropdown__section--bot'>
                        {role === 'owner' ? (
                            <div
                                className='post__options__option'
                                onClick={() => {
                                    handlePostDelete()
                                }}
                            >
                                <IconOptionsThrash />
                                <span>{itemTitle()}</span>
                            </div>
                        ) : (
                            <div
                                className='post__options__option'
                                onClick={() => {
                                    handlePostReport()
                                }}
                            >
                                <IconOptionsReport />
                                <span>{t('reportPost')}</span>
                            </div>
                        )}
                    </div>
                </OptionsDropdownWrapper>
            </div>
        </>
    )
}

const BackgroundContainer = styled.div`
    position: fixed;
    inset: 0;
    z-index:15;

    background-color: transparent;

    @media (min-width: ${screens.medium}) {
        background: rgba(0, 0, 0, 0.25);
    }
`
interface OptionsDropdownWrapperProps {
    $withoutBottomNavigation: boolean
}

const OptionsDropdownWrapper = styled.div<OptionsDropdownWrapperProps>`
    @media (max-width: ${screens.medium}) {
        position: fixed;
        z-index: 15;
        padding-bottom: 3rem;
    }

    ${props =>
        props.$withoutBottomNavigation &&
        `padding-bottom: 2rem !important;`}

    @media (min-width: ${screens.medium}) {
        position: fixed;
        z-index: 15;

        top: 50%;
        left: 50%;
        right: auto;
        bottom: auto;

        width: 100%;
        max-width: 400px;

        background: #fff;
        border-radius: 2.4rem;
        padding: 3rem 0;

        transform: translate3d(-50%, -50%, 0) scale(0.9);
        transition: all ease-in-out 0.3s;

        box-shadow: 0 2rem 6rem rgba(17, 29, 41, 0.25);

        &.post__options__dropdown--active {
            transform: translate3d(-50%, -50%, 0) scale(1);
        }
    }
`